import { Trans } from "@lingui/macro";
import { ReactNode, useCallback, useState } from "react";

import { ExpandableRow } from "components/ExpandableRow";

export default function EarnFaq() {
  return (
    <div className="flex flex-col gap-12 rounded-8 bg-slate-900 p-20">
      <h4 className="text-16 font-medium text-typography-primary">
        <Trans>FAQ</Trans>
      </h4>
      <div className="flex flex-col">
        <EarnFaqItem
          question={<Trans>Where do HFDX staking rewards come from?</Trans>}
          answer={
            <Trans>
              HFDX staking rewards are generated through a 'buyback and distribute' process. HFDX tokens are purchased on
              the open market using the revenue generated by the protocol. These tokens are then distributed back to
              stakers. No new HFDX tokens are minted during this process.
            </Trans>
          }
        />
        <EarnFaqItem
          question={<Trans>What's the difference between providing liquidity in GLV or GM?</Trans>}
          answer={
            <Trans>
              GLV and GM both enable liquidity provision on HFDX, allowing users to earn fees from three revenue sources
              that auto-compound into the token's price. GM tokens represent liquidity in a single pool that supports
              one specific market on HFDX. GLV vaults contain multiple GM tokens in an auto-rebalancing composition,
              thereby aggregating liquidity across various markets that have the same backing tokens (ex: ETH-USDC, or
              BTC-USDC). GM enables users to provide isolated liquidity with exposure to a specific market, while GLV
              allows users to passively earn yield from a diverse range of highly utilized pools.
            </Trans>
          }
        />
        <EarnFaqItem
          question={<Trans>Can I unstake anytime?</Trans>}
          answer={
            <Trans>
              Yes, staking and providing liquidity are fully permissionless. The HFDX, GLV, and GM tokens are liquid and
              can be unstaked and withdrawn at any time. For LP tokens, there could be some rare edge cases where your
              liquidity is being used for active trades. In that case, you can withdraw as soon as positions close.
            </Trans>
          }
        />
        <EarnFaqItem
          question={<Trans>What networks are supported?</Trans>}
          answer={
            <Trans>
              Currently, staking HFDX is only supported on Avalanche and Arbitrum. For providing liquidity, different GLV
              vaults and GM tokens are available on each blockchain. Please visit the Pools page for an overview of the
              liquidity products that are available on the chain you're connected to.
            </Trans>
          }
        />
      </div>
    </div>
  );
}

function EarnFaqItem({ question, answer }: { question: ReactNode; answer: ReactNode }) {
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen((stateOpen) => !stateOpen);
  }, []);

  return (
    <ExpandableRow
      open={open}
      onToggle={handleToggle}
      title={question}
      contentClassName="text-13 text-typography-primary"
      handleClassName="text-body-medium pt-16 text-left font-medium"
      className="border-b-1/2 border-slate-600 pb-16 last:border-b-0 last:pb-0"
      chevronClassName="mr-8 mt-12"
    >
      {answer}
    </ExpandableRow>
  );
}
